import React, { FC, useEffect, useState } from 'react';
import { Checkbox, Col, DatePicker, Form, Input, Row, Select, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { i18nKey } from 'src/locales/i18n';
import styles from './contact-person.module.less';
import TitleBox from 'src/components/title-box/title-box';
import { ExclamationCircleFilled } from '@ant-design/icons';
import {
	CHINA_NATIONALITY_VALUE,
	DEAFULT_COUNTRY_CODE_CHINESE,
	HONG_KONG_NATIONALITY_VALUE,
	OTHER_VALUE_TYPE
} from 'src/constants';
import { IContactPerson } from 'src/interfaces/accounts/company';
import useStore from 'src/hooks/useStore';
import { IApplicationStore } from 'src/store/root/application.store';
import moment from 'moment';
import { ICorporateOnboardingStore } from 'src/store/corporate/onboarding.store';
import { observer } from 'mobx-react-lite';
// import { IOnboardingStore } from 'src/store/user/onboarding.store';
// import { scrollPageToTop } from 'src/helpers/window';
import {
	isHkIdValid,
	normalizeHkCheckDigitPart,
	normalizeHkIdFirstPart,
	normalizeHkIdPart,
	normalizeInputBlockCharacter,
	normalizeInputBlockNumber,
	normalizeInputBlockNumberAndAllowChineseCharacter,
	normalizeInputBlockNumberOnly
} from 'src/helpers/common.utils';
import CountryDistrictSection from 'src/components/country-district-section/country-district-section';
import {
	CHINESE_ID_LENGTH,
	CHINESE_MOBILE_LENGTH,
	EMAIL_VALIDATION_RULES,
	LETTER_REGEX,
	MAXIMUM_LIVED_YEAR_REGEX,
	NOT_NUMBER_REGEX,
	NUMBER_REGEX,
	ZIPCODE_REGEX
} from 'src/constants/validation';
import { RangePickerProps } from 'antd/lib/date-picker';
import { checkChineseCharacters, checkLengthCharacters, isValidPassport } from 'src/utils/common.utils';
import { FormInstance } from 'antd/es/form/Form';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';

export interface IContactPersonProps {
	accountForm: FormInstance;
}

const ContactPerson: FC<IContactPersonProps> = ({ accountForm }) => {
	const applicationStore: IApplicationStore = useStore('applicationStore');
	const corporateOnboardingStore: ICorporateOnboardingStore = useStore('corporateOnboardingStore');

	const [isMandatoryChineseId, setMandatoryChineseId] = useState<boolean>(false);

	const [isMandatoryHkId, setMandatoryHkId] = useState<boolean>(false);

	const [isMandatoryPassport, setMandatoryPassport] = useState<boolean>(false);

	const [saving, setSaving] = useState(false);

	const [disableZipCode, setDisableZipCode] = useState(false);
	const [disablEmailingZipCode, setDisablEmailingZipCode] = useState(false);
	const [isRequireChineseName, setRequireChineseName] = useState<boolean>(false);
	const [isChineseNationality, setChineseNationality] = useState<boolean>(false);
	const [t] = useTranslation();

	const onChangeNationality = (value: string) => {
		corporateOnboardingStore.updateContactPerson({ nationality: value });
		switch (value) {
			case HONG_KONG_NATIONALITY_VALUE:
				setMandatoryHkId(true);
				setMandatoryPassport(false);
				setMandatoryChineseId(false);
				setRequireChineseName(false);
				setChineseNationality(false);
				break;
			case CHINA_NATIONALITY_VALUE:
				setMandatoryChineseId(true);
				setMandatoryHkId(false);
				setMandatoryPassport(false);
				setRequireChineseName(true);
				setChineseNationality(true);
				accountForm.setFieldValue(['contactPerson', 'gender'], undefined);
				accountForm.setFieldValue(['contactPerson', 'mobilePhoneCountryCode'], DEAFULT_COUNTRY_CODE_CHINESE);
				break;
			default:
				setMandatoryPassport(true);
				setMandatoryChineseId(false);
				setMandatoryHkId(false);
				setRequireChineseName(false);
				setChineseNationality(false);
				accountForm.setFieldValue(['contactPerson', 'mobilePhoneCountryCode'], null);
				break;
		}
	};

	const disabledDate: RangePickerProps['disabledDate'] = (current) => {
		// Can not select days before today and today
		return current && current > moment().subtract(18, 'years').endOf('day');
	};

	const checkHonKongId = (changeValue: Partial<IContactPerson>, values: IContactPerson) => {
		if (changeValue?.hkId || changeValue?.hkIdPart1 || changeValue?.hkIdPart2) {
			if (!values.hkId && !values.hkIdPart1 && !values.hkIdPart2) {
				accountForm.setFields([
					{ name: ['contactPerson', 'hkIdPart1'], validating: false, errors: ['This is Mandatory'] }
				]);
			}
		}
	};

	const checkDisableZipCode = (values: {
		contactPerson: { mailingAddress: { country: string }; residentialAddress: { country: string } };
	}) => {
		setDisableZipCode(values?.contactPerson?.residentialAddress?.country === 'HK');
		setDisablEmailingZipCode(values?.contactPerson?.mailingAddress?.country === 'HK');
	};

	const onChangeValuesForm = (
		changeValue: Partial<IContactPerson>,
		values: IContactPerson & {
			contactPerson: { mailingAddress: { country: string }; residentialAddress: { country: string } };
		}
	) => {
		checkHonKongId(changeValue, values);
		checkDisableZipCode(values);

		if (values.maritalStatus) {
			corporateOnboardingStore.updateMaritalStatus(values.maritalStatus);

			if (values.maritalStatus !== OTHER_VALUE_TYPE.OTHER_VALUE) {
				accountForm.setFieldValue(['contactPerson', 'otherMaritalStatus'], undefined);
			}
		}
		// if (values.contactPerson?.title) {
		// 	corporateOnboardingStore.updateTitle(values.contactPerson?.title);
		// 	if (values.title !== OTHER_VALUE_TYPE.OTHERS_VALUE) {
		// 		accountForm.setFieldValue(['contactPerson', 'titleOther'], undefined);
		// 	}
		// }
		if (values.contactPerson?.sourceOfFunds) {
			corporateOnboardingStore.updateSourceOfFunds(values.contactPerson?.sourceOfFunds);
			if (!values.contactPerson?.sourceOfFunds.includes(OTHER_VALUE_TYPE.OTHER_SOURCE_OF_FUNDS)) {
				accountForm.setFieldValue('otherSourceOfFunds', undefined);
				corporateOnboardingStore.clearOtherInput('otherSourceOfFunds');
			}
		}
	};

	const onChangeSourceOfFund = (e: CheckboxValueType[]) => {
		corporateOnboardingStore.updateSourceOfFunds(e as string[]);
	};
	const onChangeTitle = (title: string) => {
		corporateOnboardingStore.updateContactPerson({ title });
		accountForm.resetFields(['contactPerson', 'gender']);
		accountForm.setFieldValue(['contactPerson', 'title'], title);
		accountForm.setFieldValue(['contactPerson', 'nationality'], corporateOnboardingStore.contractPerson?.nationality);
	};

	const renderTitle = () => {
		if (corporateOnboardingStore.contractPerson?.title === OTHER_VALUE_TYPE.OTHERS_VALUE) {
			return (
				<>
					<Col xs={24} sm={24} md={24} lg={12}>
						<Form.Item required={true} label={`${t(i18nKey.dashboard.accountInformation.title)}`}>
							<Row gutter={10}>
								<Col span={12}>
									<Form.Item
										noStyle
										name={['contactPerson', 'title']}
										required={false}
										rules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											}
										]}>
										<Select
											onChange={onChangeTitle}
											placeholder={`${t(i18nKey.common.select)}`}
											options={applicationStore.titleOptions}
										/>
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item
										name={['contactPerson', 'titleOther']}
										rules={[
											{
												required:
													corporateOnboardingStore.contractPerson?.title === OTHER_VALUE_TYPE.OTHERS_VALUE
														? true
														: false,
												message: `${t(i18nKey.onboarding.validation.required)}`
											}
										]}>
										<Input />
									</Form.Item>
								</Col>
							</Row>
						</Form.Item>
					</Col>
				</>
			);
		}

		return (
			<>
				<Col xs={24} sm={24} md={24} lg={12}>
					<Form.Item
						name={['contactPerson', 'title']}
						label={`${t(i18nKey.dashboard.accountInformation.title)}`}
						required={true}
						rules={[
							{
								required: true,
								message: `${t(i18nKey.onboarding.validation.required)}`
							}
						]}>
						<Select
							onChange={onChangeTitle}
							placeholder={`${t(i18nKey.common.select)}`}
							options={applicationStore.titleOptions}
						/>
					</Form.Item>
				</Col>
			</>
		);
	};

	useEffect(() => {
		setSaving(false);
		corporateOnboardingStore.loadProgress().then(() => {
			setSaving(false);
		});
		// scrollPageToTop();
	}, []);
	useEffect(() => {
		if (!accountForm.isFieldsTouched()) {
			accountForm.setFieldsValue({
				...corporateOnboardingStore.contractPerson
			});

			if (corporateOnboardingStore.contractPerson?.maritalStatus !== OTHER_VALUE_TYPE.OTHER_VALUE) {
				accountForm.setFieldValue(['contactPerson', 'otherMaritalStatus'], undefined);
			}

			if (!corporateOnboardingStore.contractPerson?.sourceOfFunds?.includes(OTHER_VALUE_TYPE.OTHER_SOURCE_OF_FUNDS)) {
				accountForm.setFieldValue(
					['contactPerson', 'otherSourceOfFunds'],
					corporateOnboardingStore.contractPerson?.otherSourceOfFunds
				);
			}

			if (corporateOnboardingStore.contractPerson?.title !== OTHER_VALUE_TYPE.OTHERS_VALUE) {
				accountForm.setFieldValue(['contactPerson', 'titleOther'], undefined);
			}

			if (corporateOnboardingStore.contractPerson?.country === 'HK') {
				setDisableZipCode(true);
			}

			if (corporateOnboardingStore.contractPerson?.mailingCountry === 'HK') {
				setDisablEmailingZipCode(true);
			}
			switch (corporateOnboardingStore.contractPerson?.nationality) {
				case HONG_KONG_NATIONALITY_VALUE:
					setMandatoryHkId(true);
					setMandatoryPassport(false);
					setMandatoryChineseId(false);
					setRequireChineseName(false);
					break;
				case CHINA_NATIONALITY_VALUE:
					setMandatoryChineseId(true);
					setMandatoryHkId(false);
					setMandatoryPassport(false);
					setRequireChineseName(true);
					break;
				case undefined:
					setMandatoryChineseId(false);
					setMandatoryHkId(false);
					setMandatoryPassport(false);
					setRequireChineseName(false);
					break;
				default:
					setMandatoryPassport(true);
					setMandatoryChineseId(false);
					setMandatoryHkId(false);
					setRequireChineseName(false);
					break;
			}
		}
	}, [corporateOnboardingStore.contractPerson]);

	return (
		<Spin spinning={saving}>
			<div className="common-padding">
				<Form
					form={accountForm}
					layout="vertical"
					onValuesChange={onChangeValuesForm}
					initialValues={{
						...corporateOnboardingStore.contractPerson
					}}>
					<Row align="middle" gutter={[0, 0]}>
						<Col span={24}>
							<Typography.Text className={styles.titleSection}>
								<ExclamationCircleFilled
									style={{
										color: '#22A5B4',
										cursor: 'pointer',
										paddingLeft: '0.5rem',
										fontSize: '2rem'
									}}
								/>
								<span>{t(i18nKey.onboarding.corporate.form.notice)}</span>
							</Typography.Text>
						</Col>
					</Row>
					<Spin spinning={saving}>
						<div className={styles.subLevelForm}>
							<Row gutter={[32, 16]}>
								{renderTitle()}
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item
										name={['contactPerson', 'firstName']}
										label={`${t(i18nKey.registration.firstName)}`}
										required={true}
										normalize={normalizeInputBlockNumber}
										rules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											},
											{
												pattern: LETTER_REGEX,
												message: `${t(i18nKey.registration.validation.inputOnlyAlphabet)}`
											}
										]}>
										<Input placeholder="-" />
									</Form.Item>
								</Col>
							</Row>
							<Row gutter={[32, 16]}>
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item
										name={['contactPerson', 'middleName']}
										label={`${t(i18nKey.registration.middleName)}`}
										normalize={normalizeInputBlockNumber}
										rules={[
											{
												pattern: LETTER_REGEX,
												message: `${t(i18nKey.registration.validation.inputOnlyAlphabet)}`
											}
										]}>
										<Input placeholder="-" />
									</Form.Item>
								</Col>
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item
										name={['contactPerson', 'lastName']}
										label={`${t(i18nKey.registration.lastName)}`}
										required={true}
										normalize={normalizeInputBlockNumber}
										rules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											},
											{
												pattern: LETTER_REGEX,
												message: `${t(i18nKey.registration.validation.inputOnlyAlphabet)}`
											}
										]}>
										<Input placeholder="-" />
									</Form.Item>
								</Col>
							</Row>
							<Row gutter={[32, 16]}>
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item
										name={['contactPerson', 'nationality']}
										label={`${t(i18nKey.dashboard.accountInformation.nationality)}`}
										required={true}
										rules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											}
										]}>
										<Select
											onChange={onChangeNationality}
											placeholder={`${t(i18nKey.common.select)}`}
											options={applicationStore.nationalities}
											showSearch
											filterOption={(input, option) =>
												new RegExp(`^${input.toLowerCase().trim()}`).test((option?.label ?? '').toLowerCase())
											}
										/>
									</Form.Item>
								</Col>
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item
										name={['contactPerson', 'chineseName']}
										label={`${t(i18nKey.dashboard.accountInformation.chineseName)}`}
										normalize={normalizeInputBlockNumberAndAllowChineseCharacter}
										required={isRequireChineseName}
										rules={[
											{
												required: isRequireChineseName,
												message: `${t(i18nKey.onboarding.validation.required)}`
											},
											{
												validator: checkChineseCharacters
											}
										]}>
										<Input />
									</Form.Item>
								</Col>
							</Row>
							<Row gutter={[32, 16]}>
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item
										name={['contactPerson', 'dob']}
										label={`${t(i18nKey.dashboard.accountInformation.dob)}`}
										required={true}
										rules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											}
										]}>
										<DatePicker disabledDate={disabledDate} placeholder="Select Date" className={styles.date} />
									</Form.Item>
								</Col>
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item
										name={['contactPerson', 'placeOfBirth']}
										label={`${t(i18nKey.dashboard.accountInformation.pob)}`}
										required={true}
										rules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											}
										]}>
										<Select
											placeholder={`${t(i18nKey.common.select)}`}
											options={applicationStore.listOfCountry}
											showSearch
											filterOption={(input, option) =>
												new RegExp(`^${input.toLowerCase().trim()}`).test((option?.label ?? '').toLowerCase())
											}
										/>
									</Form.Item>
								</Col>
							</Row>
							<Row gutter={[32, 16]}>
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item
										name={['contactPerson', 'gender']}
										label={`${t(i18nKey.dashboard.accountInformation.gender)}`}
										required={true}
										rules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											}
										]}>
										<Select
											placeholder={`${t(i18nKey.common.select)}`}
											allowClear
											options={applicationStore.filterGenderByTitle(
												isChineseNationality,
												corporateOnboardingStore.contractPerson?.title as string
											)}
										/>
									</Form.Item>
								</Col>
								{isMandatoryHkId && (
									<Col xs={24} sm={24} md={24} lg={12}>
										<Form.Item required={isMandatoryHkId} label={`${t(i18nKey.dashboard.accountInformation.hkId)}`}>
											<Row align="middle" gutter={16}>
												<Col span={6}>
													<Form.Item
														name={['contactPerson', 'hkIdPart1']}
														noStyle
														dependencies={[
															['contactPerson', 'hkId'],
															['contactPerson', 'hkIdPart2']
														]}
														normalize={normalizeHkIdFirstPart}
														required={true}
														rules={[
															(fInstance) => ({
																validator() {
																	if (
																		!fInstance.getFieldValue(['contactPerson', 'hkIdPart1']) &&
																		!fInstance.getFieldValue(['contactPerson', 'hkId']) &&
																		!fInstance.getFieldValue(['contactPerson', 'hkIdPart2'])
																	) {
																		return Promise.reject(`${t(i18nKey.onboarding.validation.required)}`);
																	}

																	if (
																		!isHkIdValid(
																			fInstance,
																			['contactPerson', 'hkIdPart1'],
																			['contactPerson', 'hkId'],
																			['contactPerson', 'hkIdPart2']
																		)
																	) {
																		return Promise.reject(
																			new Error(
																				`${t(i18nKey.onboarding.validation.accountDetail.required.hkIDNoFormat)}`
																			)
																		);
																	}
																	return Promise.resolve();
																}
															})
														]}>
														<Input />
													</Form.Item>
												</Col>
												<Col span={12}>
													<Form.Item
														name={['contactPerson', 'hkId']}
														dependencies={[
															['contactPerson', 'hkIdPart1'],
															['contactPerson', 'hkIdPart2']
														]}
														noStyle
														required={true}
														normalize={normalizeHkIdPart}>
														<Input
														// onChange={(e) => {
														// 	if (!e.target.value) {
														// 		setMandatoryHkId(false);
														// 	} else {
														// 		setMandatoryHkId(true);
														// 	}
														// }}
														/>
													</Form.Item>
												</Col>
												<Col span={6}>
													<Row align="middle">
														<Col xs={2} sm={2} md={1}>
															<span>{'('}</span>
														</Col>
														<Col xs={21} sm={21} md={21}>
															<Form.Item
																name={['contactPerson', 'hkIdPart2']}
																noStyle
																normalize={normalizeHkCheckDigitPart}>
																<Input
																// onChange={(e) => {
																// 	if (!e.target.value) {
																// 		setMandatoryHkId(false);
																// 	} else {
																// 		setMandatoryHkId(true);
																// 	}
																// }}
																/>
															</Form.Item>
														</Col>
														<Col xs={1} sm={1} md={1}>
															<span>{')'}</span>
														</Col>
													</Row>
												</Col>
											</Row>
										</Form.Item>
									</Col>
								)}
								{isMandatoryChineseId && (
									<Col xs={24} sm={24} md={24} lg={12}>
										<Form.Item
											name={['contactPerson', 'chineseIdNo']}
											label={`${t(i18nKey.dashboard.accountInformation.chineseId)}`}
											required={isMandatoryChineseId}
											normalize={normalizeInputBlockCharacter}
											rules={[
												() => ({
													validator(_, value) {
														if (!value && isMandatoryChineseId) {
															return Promise.reject(new Error(`${t(i18nKey.onboarding.validation.required)}`));
														}
														return Promise.resolve();
													}
												}),
												() => ({
													validator(_, value) {
														return checkLengthCharacters(value, CHINESE_ID_LENGTH, isChineseNationality);
													}
												})
											]}>
											<Input />
										</Form.Item>
									</Col>
								)}
								{isMandatoryPassport && (
									<Col xs={24} sm={24} md={24} lg={12}>
										<Form.Item
											name={['contactPerson', 'passportNo']}
											label={`${t(i18nKey.dashboard.accountInformation.passport)}`}
											required={isMandatoryPassport}
											rules={[
												{
													required: isMandatoryPassport,
													message: `${t(i18nKey.onboarding.validation.required)}`
												},
												() => ({
													validator(_, value) {
														if (!value) return Promise.resolve();
														if (value && !isValidPassport(value)) {
															return Promise.reject(new Error('Incorrect passport No.'));
														}
														return Promise.resolve();
													}
												})
											]}>
											<Input />
										</Form.Item>
									</Col>
								)}
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item
										name={['contactPerson', 'countryTaxResidence']}
										label={`${t(i18nKey.dashboard.accountInformation.countryOfTax)}`}
										required={true}
										rules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											}
										]}>
										<Select
											placeholder={`${t(i18nKey.common.select)}`}
											options={applicationStore.listOfCountry}
											showSearch
											filterOption={(input, option) =>
												new RegExp(`^${input.toLowerCase().trim()}`).test((option?.label ?? '').toLowerCase())
											}
										/>
									</Form.Item>
								</Col>
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item
										name={['contactPerson', 'positionInCompany']}
										label={`${t(i18nKey.dashboard.accountInformation.positionInCompany)}`}
										required={true}
										normalize={normalizeInputBlockNumberOnly}
										rules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											},
											{
												pattern: NOT_NUMBER_REGEX,
												message: `${t(i18nKey.common.validate.onlyCharacters)}`
											}
										]}>
										<Input />
									</Form.Item>
								</Col>
							</Row>
							<Form.Item
								name={['contactPerson', 'sourceOfFunds']}
								label={`${t(i18nKey.dashboard.accountInformation.sourceOfFunds)}`}
								className={styles.formSourceOfFunds}
								required={true}
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<Checkbox.Group style={{ width: '100%' }} onChange={onChangeSourceOfFund}>
									<Row gutter={[16, 16]} align={'middle'}>
										<Col xxl={4} xl={6} md={12} xs={24} className={styles.alignMiddle}>
											<Checkbox value={'1'}>Investment Income</Checkbox>
										</Col>
										<Col xxl={4} xl={6} md={12} xs={24} className={styles.alignMiddle}>
											<Checkbox value={'2'}>Business Income</Checkbox>
										</Col>
										<Col xxl={3} xl={6} md={12} xs={24} className={styles.alignMiddle}>
											<Checkbox value={'3'}>Salary</Checkbox>
										</Col>
										<Col xxl={4} xl={6} md={12} xs={24} className={styles.alignMiddle}>
											<Checkbox value={'4'}>Director Fee</Checkbox>
										</Col>
										<Col xxl={4} xl={6} md={12} xs={24} className={styles.alignMiddle}>
											<Checkbox value={'5'}>Dividend</Checkbox>
										</Col>
										<Col xxl={5} xl={6} md={12} xs={24} className={styles.alignMiddle}>
											<Row wrap={false} align="middle">
												<Checkbox value={'6'}>Others</Checkbox>

												{corporateOnboardingStore.contractPerson?.sourceOfFunds?.includes(
													OTHER_VALUE_TYPE.OTHER_SOURCE_OF_FUNDS
												) && (
													<>
														<Col flex={'0 0 150px'} className={styles.alignMiddle}>
															<Form.Item
																noStyle
																name={['contactPerson', 'otherSourceOfFunds']}
																rules={[
																	{
																		required: corporateOnboardingStore.contractPerson?.sourceOfFunds?.includes(
																			OTHER_VALUE_TYPE.OTHER_SOURCE_OF_FUNDS
																		)
																			? true
																			: false,
																		message: `${t(i18nKey.onboarding.validation.required)}`
																	}
																]}>
																<Input />
															</Form.Item>
														</Col>
													</>
												)}
											</Row>
										</Col>
									</Row>
								</Checkbox.Group>
							</Form.Item>
						</div>
						<TitleBox style={{ backgroundColor: '#F7F7FF', color: '#009DAD' }}>
							<span>{t(i18nKey.dashboard.accountInformation.contactDetail)}</span>
						</TitleBox>
						<div className={styles.subLevelForm}>
							<Row gutter={[32, 16]}>
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item required={false} label={`${t(i18nKey.dashboard.accountInformation.officePhoneNo)}`}>
										<Row gutter={[16, 16]}>
											<Col xs={9} sm={9} md={6} lg={11} xl={9} xxl={7}>
												<Form.Item
													noStyle
													name={['contactPerson', 'officePhoneCountryCode']}
													normalize={normalizeInputBlockCharacter}>
													<Select
														placeholder={`${t(i18nKey.common.select)}`}
														options={applicationStore.phoneCountryCode}
														showSearch
														filterOption={(input, option) =>
															(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
														}
													/>
												</Form.Item>
											</Col>
											<Col xs={15} sm={15} md={18} lg={13} xl={15} xxl={17}>
												<Form.Item
													noStyle
													name={['contactPerson', 'officePhone']}
													normalize={normalizeInputBlockCharacter}>
													<Input />
												</Form.Item>
											</Col>
										</Row>
									</Form.Item>
								</Col>
								<Col xs={24} sm={24} md={24} lg={12}>
									<div>
										<Typography.Text className="styleLabelForm">
											{`${t(i18nKey.dashboard.accountInformation.mobilePhone)}`}
											<span style={{ color: 'red' }}>*</span>
										</Typography.Text>
										<Row gutter={[16, 16]}>
											<Col xs={9} sm={9} md={6} lg={11} xl={9} xxl={7}>
												<Form.Item
													name={['contactPerson', 'mobilePhoneCountryCode']}
													normalize={normalizeInputBlockCharacter}
													required={false}
													rules={[
														{
															required: true,
															message: `${t(i18nKey.onboarding.validation.required)}`
														}
													]}>
													<Select
														placeholder={`${t(i18nKey.common.select)}`}
														options={applicationStore.phoneCountryCode}
														disabled={isChineseNationality}
														showSearch
														filterOption={(input, option) =>
															(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
														}
													/>
												</Form.Item>
											</Col>
											<Col xs={15} sm={15} md={18} lg={13} xl={15} xxl={17}>
												<Form.Item
													name={['contactPerson', 'mobilePhone']}
													normalize={normalizeInputBlockCharacter}
													required={false}
													rules={[
														{
															required: true,
															message: `${t(i18nKey.onboarding.validation.required)}`
														},
														() => ({
															validator(_, value) {
																return checkLengthCharacters(value, CHINESE_MOBILE_LENGTH, isChineseNationality);
															}
														})
													]}>
													<Input />
												</Form.Item>
											</Col>
										</Row>
									</div>
								</Col>
							</Row>
						</div>

						<div className={styles.subLevelForm}>
							<Row gutter={[32, 16]}>
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item
										name={['contactPerson', 'email']}
										label={`${t(i18nKey.dashboard.accountInformation.emailAddress)}`}
										required={true}
										rules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											},
											{
												pattern: EMAIL_VALIDATION_RULES.REGEX,
												message: `${t(i18nKey.registration.validation.wrongEmailFormat)}`
											}
										]}>
										<Input />
									</Form.Item>
								</Col>
							</Row>
						</div>

						<div className={styles.subLevelForm}>
							<Row gutter={[32, 16]}>
								<Col span={24}>
									<Row>
										<Col span={24} className={styles.titleName}>
											{t(i18nKey.dashboard.accountInformation.residentAddressReg)}
										</Col>
									</Row>
									<Row>
										<Col span={24}>
											<div className={styles.dividerLine}></div>
										</Col>
									</Row>

									<Row gutter={[32, 16]}>
										<Col span={24}>
											<Form.Item
												name={['contactPerson', 'residentialAddress', 'addressLine']}
												label={`${t(i18nKey.dashboard.accountInformation.adrLine)}`}
												required={true}
												rules={[
													{
														required: true,
														message: `${t(i18nKey.onboarding.validation.required)}`
													}
												]}>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Row gutter={[32, 16]}>
										<Col span={24}>
											<Form.Item
												name={['contactPerson', 'residentialAddress', 'street']}
												label={`${t(i18nKey.dashboard.accountInformation.street)}`}
												required={true}
												rules={[
													{
														required: true,
														message: `${t(i18nKey.onboarding.validation.required)}`
													}
												]}>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<CountryDistrictSection
										countryName={['contactPerson', 'residentialAddress', 'country']}
										countryLabel={`${t(i18nKey.dashboard.accountInformation.country)}`}
										requireCountry={true}
										countryRules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											}
										]}
										districtName={['contactPerson', 'residentialAddress', 'district']}
										requireDistrict={true}
										districtLabel={`${t(i18nKey.dashboard.accountInformation.district)}`}
										districtRules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											}
										]}
									/>

									{!disableZipCode && (
										<Row gutter={[32, 16]}>
											<Col span={24}>
												<Form.Item
													name={['contactPerson', 'residentialAddress', 'zipCode']}
													label={`${t(i18nKey.dashboard.accountInformation.zipCode)}`}
													required={true}
													rules={[
														{
															required: true,
															message: `${t(i18nKey.onboarding.validation.required)}`
														},
														{
															pattern: ZIPCODE_REGEX,
															message: `${t(i18nKey.onboarding.validation.zipCode)}`
														}
													]}>
													<Input />
												</Form.Item>
											</Col>
										</Row>
									)}
									<Row>
										<div className="ant-col ant-form-item-label">
											<label htmlFor="">
												{t(i18nKey.dashboard.accountInformation.liveYears)}
												<span className={styles.requireAsterisk}>*</span>
											</label>
										</div>
									</Row>
									<Row gutter={8} className={styles.yearRow}>
										<Col xs={12} sm={12} md={12} lg={3}>
											<Form.Item
												name={['contactPerson', 'residentialAddress', 'livedTime']}
												required={false}
												normalize={normalizeInputBlockCharacter}
												rules={[
													{
														required: true,
														message: `${t(i18nKey.onboarding.validation.required)}`
													},
													{
														pattern: NUMBER_REGEX,
														message: `${t(i18nKey.onboarding.validation.bankAccount.required.onlyNumber)}`
													},
													{
														pattern: MAXIMUM_LIVED_YEAR_REGEX,
														message: `${t(i18nKey.onboarding.validation.accountDetail.required.maximumLiveYear)}`
													}
												]}>
												<Input />
											</Form.Item>
										</Col>
										<Col xs={12} sm={12} md={12} lg={5}>
											<Typography.Title level={4}>{t(i18nKey.dashboard.accountInformation.year)}</Typography.Title>
										</Col>
									</Row>
								</Col>
							</Row>
						</div>

						<div className={styles.subLevelForm}>
							<Row gutter={[32, 16]}>
								<Col span={24}>
									<Row>
										<Col span={24} className={styles.titleName}>
											{t(i18nKey.dashboard.accountInformation.mailingAddressReg)}
										</Col>
									</Row>
									<Row>
										<Col span={24}>
											<div className={styles.dividerLine}></div>
										</Col>
									</Row>

									<Row gutter={[32, 16]}>
										<Col span={24}>
											<Form.Item
												name={['contactPerson', 'mailingAddress', 'addressLine']}
												label={`${t(i18nKey.dashboard.accountInformation.adrLine)}`}>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Row gutter={[32, 16]}>
										<Col span={24}>
											<Form.Item
												name={['contactPerson', 'mailingAddress', 'street']}
												label={`${t(i18nKey.dashboard.accountInformation.street)}`}>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<CountryDistrictSection
										countryName={['contactPerson', 'mailingAddress', 'country']}
										countryLabel={`${t(i18nKey.dashboard.accountInformation.country)}`}
										districtName={['contactPerson', 'mailingAddress', 'district']}
										districtLabel={`${t(i18nKey.dashboard.accountInformation.district)}`}
									/>
									{!disablEmailingZipCode && (
										<Row gutter={[32, 16]}>
											<Col span={24}>
												<Form.Item
													name={['contactPerson', 'mailingAddress', 'zipCode']}
													label={`${t(i18nKey.dashboard.accountInformation.zipCode)}`}
													rules={[
														{
															pattern: ZIPCODE_REGEX,
															message: `${t(i18nKey.onboarding.validation.zipCode)}`
														}
													]}>
													<Input />
												</Form.Item>
											</Col>
										</Row>
									)}
								</Col>
							</Row>
						</div>
					</Spin>
				</Form>
			</div>
		</Spin>
	);
};

export default observer(ContactPerson);
